<template>
  <div class="map-toolbar">
    <!-- Left group: weather + datetime + stats (estilo pencil) -->
    <div class="toolbar-datetime">
      <AppLauncher />
      <Weather :user="user" />
      <span class="toolbar-clock">
        <span class="toolbar-clock__date">{{ dateStr }}</span>
        <span class="toolbar-clock__time">{{ timeStr }}</span>
      </span>
      <!-- Stats chips: Incidentes, GPS, Transporte Público, Analíticos, Botones de pánico.
           Bajo 1500px de viewport el contenedor se encoge a ~2 chips visibles y
           se navega con las flechas; sobre 1500px las flechas se ocultan vía
           CSS y todos los chips quedan en una sola línea sin scroll. -->
      <div class="toolbar-stats" v-if="statChips.length">
        <button
          type="button"
          class="toolbar-stats__nav toolbar-stats__nav--prev"
          v-auto-tooltip="'Ver más'"
          @click="scrollChips('statsScroll', -1)"
        >
          <v-icon x-small>mdi-chevron-left</v-icon>
        </button>
        <div ref="statsScroll" class="toolbar-stats__scroll">
          <span
            v-for="chip in statChips"
            :key="chip.type"
            class="toolbar-stat"
            v-auto-tooltip="chip.label"
          >
            <v-icon x-small class="toolbar-stat__icon" :color="chip.color">{{ chip.icon }}</v-icon>
            <span class="toolbar-stat__count">{{ chip.count.toLocaleString('es-MX') }}</span>
          </span>
        </div>
        <button
          type="button"
          class="toolbar-stats__nav toolbar-stats__nav--next"
          v-auto-tooltip="'Ver más'"
          @click="scrollChips('statsScroll', 1)"
        >
          <v-icon x-small>mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>

    <!-- Center group: style + zoom + layers + filtros + KPIs (centrado) -->
    <div class="toolbar-center-group">
    <!-- Style toggle -->
    <v-btn-toggle
      :value="styleId"
      mandatory
      dense
      class="toolbar-group toolbar-style-toggle"
      @change="onStyleChange"
    >
      <v-btn v-if="isLightTheme" value="light" small class="toolbar-style-btn" v-auto-tooltip="'Modo diurno'">
        <v-icon small>mdi-weather-sunny</v-icon>
      </v-btn>
      <v-btn v-if="!isLightTheme" value="dark" small class="toolbar-style-btn" v-auto-tooltip="'Modo oscuro'">
        <v-icon small>mdi-weather-night</v-icon>
      </v-btn>
      <v-btn value="satellite" small class="toolbar-style-btn" v-auto-tooltip="'Satélite'">
        <v-icon small>mdi-satellite-variant</v-icon>
      </v-btn>
      <v-btn v-if="!isLightTheme" value="streets" small class="toolbar-style-btn" v-auto-tooltip="'Calles'">
        <v-icon small>mdi-road-variant</v-icon>
      </v-btn>
    </v-btn-toggle>

    <div class="toolbar-divider" />

    <!-- Primary entity layer toggles (direct buttons) -->
    <div class="toolbar-group">
      <v-btn
        v-for="layer in primaryLayers"
        :key="layer.type"
        small
        :color="layerVisibility[layer.type] !== false ? layer.color : ''"
        :text="layerVisibility[layer.type] === false"
        v-auto-tooltip="layer.label"
        :class="[
          'toolbar-entity-btn',
          layerVisibility[layer.type] !== false ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
        ]"
        @click="toggleLayer(layer.type)"
      >
        <v-icon small>{{ layer.icon }}</v-icon>
      </v-btn>
    </div>

    <!-- Filtros por entidad -->
    <v-menu v-model="filtersMenu" :close-on-content-click="false" offset-y bottom max-height="500" :z-index="20">
      <template #activator="{ on }">
        <v-btn
          small
          :class="['toolbar-entity-btn', 'toolbar-entity-btn--inactive']"
          v-auto-tooltip="'Filtros'"
          v-on="on"
        >
          <v-icon small>mdi-filter-variant</v-icon>
        </v-btn>
      </template>
      <div class="filters-panel">
        <div class="filters-panel__title">Filtros por capa</div>
        <div class="filter-section" v-for="section in filterSections" :key="section.type">
          <div class="filter-section__header" @click="filterExpanded[section.type] = !filterExpanded[section.type]">
            <v-icon x-small class="mr-1">{{ filterExpanded[section.type] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            {{ section.label }}
          </div>
          <div v-if="filterExpanded[section.type]" class="filter-section__body">
            <template v-if="section.type === 'incident'">
              <div class="filter-label">Categoría</div>
              <div class="filter-chips">
                <v-chip v-for="c in incidentCategoryOptions" :key="c.value" x-small :outlined="!isFilterActive('incident', 'category', c.value)" :color="isFilterActive('incident', 'category', c.value) ? 'var(--kln-accent)' : ''" @click="toggleFilter('incident', 'category', c.value)">{{ c.label }}</v-chip>
              </div>
            </template>
            <template v-if="statusOptions[section.type]">
              <div class="filter-label">Estado</div>
              <div class="filter-chips">
                <v-chip v-for="s in statusOptions[section.type]" :key="s.value" x-small :outlined="!isFilterActive(section.type, 'status', s.value)" :color="isFilterActive(section.type, 'status', s.value) ? 'var(--kln-accent)' : ''" @click="toggleFilter(section.type, 'status', s.value)">{{ s.label }}</v-chip>
              </div>
            </template>
            <!-- Ámbito de cámaras: estatal (red C5) / municipal / otras.
                 El dot funciona como leyenda del color de la placa que
                 rodea al marker en el mapa. Oculto mientras el switch
                 VITE_CAMERA_OWNERSHIP no esté encendido. -->
            <template v-if="section.type === 'camera' && ownershipEnabled">
              <div class="filter-label">Ámbito</div>
              <div class="filter-chips">
                <v-chip v-for="o in ownershipOptions" :key="o.value" x-small :outlined="!isOwnershipActive(o.value)" @click="toggleOwnership(o.value)">
                  <span class="ownership-dot" :style="{ backgroundColor: o.color }" />{{ o.label }}
                </v-chip>
              </div>
            </template>
            <!-- v-autocomplete y no v-select: con muchos grupos un desplegable
                 sin busqueda obliga a recorrer la lista a mano. -->
            <div class="filter-label">Grupo</div>
            <v-autocomplete :value="entityFilters[section.type] && entityFilters[section.type].groupId" :items="groupsByEntity[section.type] || []" dense outlined hide-details clearable multiple placeholder="Todos" class="filter-group-select" :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260, offsetY: true, nudgeBottom: 4 }" @change="SET_ENTITY_FILTER({ entityType: section.type, filters: { groupId: $event || [] } })" />
          </div>
        </div>
      </div>
    </v-menu>

    <div class="toolbar-divider" />

    <!-- Heatmap toggle — densidad de incidentes últimas 24 h. Color amber
         (family warm como el mid-range del gradiente del heatmap) para
         reflejar "calor" sin chocar con el rojo/naranja de Incidentes.
         Icono mdi-chart-bubble para diferenciarlo de mdi-fire (Incendios). -->
    <v-btn
      v-if="capaOk('incident')"
      small
      :color="heatmapActive ? 'var(--kln-alert-warning)' : ''"
      :text="!heatmapActive"
      :class="[
        'toolbar-entity-btn',
        heatmapActive ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
      ]"
      v-auto-tooltip="'Mapa de calor (últimas 24 h)'"
      @click="toggleHeatmap"
    >
      <v-icon small>mdi-chart-bubble</v-icon>
    </v-btn>

    <div class="toolbar-divider" />

    <!-- Geocercas: dropdown con 4 niveles independientes (geocercas generales
         + 3 niveles de mando unico). Legacy tenia 4 toggles separados; aqui
         se unifican bajo el mismo boton con el lenguaje de chips. -->
    <v-menu v-if="geofenceFilterOptions.length" offset-y bottom :close-on-content-click="false" :z-index="20">
      <template #activator="{ on }">
        <v-btn
          small
          :color="geofencesVisible ? 'var(--kln-entity-teal)' : ''"
          :text="!geofencesVisible"
          :class="[
            'toolbar-entity-btn',
            geofencesVisible ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
          ]"
          v-auto-tooltip="'Geocercas'"
          v-on="on"
        >
          <v-icon small>mdi-vector-polygon</v-icon>
        </v-btn>
      </template>
      <div class="toolbar-submenu toolbar-submenu--wide">
        <div class="config-menu__title-row">
          <div class="toolbar-submenu__title">Geocercas</div>
          <div class="config-menu__title-actions">
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Activar todas'"
              @click="onAllGeofenceFilters(true)"
            >Todas</button>
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Desactivar todas'"
              @click="onAllGeofenceFilters(false)"
            >Ninguna</button>
          </div>
        </div>
        <div class="config-menu__chips">
          <button
            v-for="t in geofenceFilterOptions"
            :key="t.type"
            type="button"
            :class="['config-chip', geofenceFiltersEfectivos[t.type] && 'config-chip--on']"
            @click="onGeofenceFilterToggle(t.type, !geofenceFiltersEfectivos[t.type])"
          >
            <v-icon x-small class="config-chip__dot">{{ geofenceFiltersEfectivos[t.type] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
            {{ t.label }}
          </button>
        </div>
      </div>
    </v-menu>

    <!-- Submenu: Ambiente (presas, sismos, incendios) -->
    <v-menu v-if="ambienteLayers.length" offset-y bottom :close-on-content-click="false" :z-index="20">
      <template #activator="{ on }">
        <v-btn
          small
          :color="hasActiveAmbiente ? 'var(--kln-trail-line)' : ''"
          :text="!hasActiveAmbiente"
          :class="[
            'toolbar-entity-btn',
            hasActiveAmbiente ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
          ]"
          v-auto-tooltip="'Ambiente'"
          v-on="on"
        >
          <v-icon small>mdi-weather-hurricane</v-icon>
        </v-btn>
      </template>
      <div class="toolbar-submenu toolbar-submenu--wide">
        <div class="config-menu__title-row">
          <div class="toolbar-submenu__title">Ambiente</div>
          <div class="config-menu__title-actions">
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Activar todas'"
              @click="onAllLayerToggle(ambienteLayers, true)"
            >Todas</button>
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Desactivar todas'"
              @click="onAllLayerToggle(ambienteLayers, false)"
            >Ninguna</button>
          </div>
        </div>
        <div class="config-menu__chips">
          <button
            v-for="layer in ambienteLayers"
            :key="layer.type"
            type="button"
            :class="['config-chip', layerVisibility[layer.type] !== false && 'config-chip--on']"
            @click="toggleLayer(layer.type)"
          >
            <v-icon x-small class="config-chip__dot">{{ layerVisibility[layer.type] !== false ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
            {{ layer.label }}
          </button>
        </div>
      </div>
    </v-menu>

    <!-- Submenu: Otros (CPRs, sensores ext, reportantes, etc) -->
    <v-menu v-if="otrosLayers.length" offset-y bottom :close-on-content-click="false" :z-index="20">
      <template #activator="{ on }">
        <v-btn
          small
          :color="hasActiveOtros ? 'var(--kln-status-idle)' : ''"
          :text="!hasActiveOtros"
          :class="[
            'toolbar-entity-btn',
            hasActiveOtros ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
          ]"
          v-auto-tooltip="'Otros elementos'"
          v-on="on"
        >
          <v-icon small>mdi-map-marker-multiple</v-icon>
        </v-btn>
      </template>
      <div class="toolbar-submenu toolbar-submenu--wide">
        <div class="config-menu__title-row">
          <div class="toolbar-submenu__title">Otros elementos</div>
          <div class="config-menu__title-actions">
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Activar todos'"
              @click="onAllLayerToggle(otrosLayers, true)"
            >Todos</button>
            <button
              type="button"
              class="config-menu__title-action"
              v-auto-tooltip="'Desactivar todos'"
              @click="onAllLayerToggle(otrosLayers, false)"
            >Ninguno</button>
          </div>
        </div>
        <div class="config-menu__chips">
          <button
            v-for="layer in otrosLayers"
            :key="layer.type"
            type="button"
            :class="['config-chip', layerVisibility[layer.type] !== false && 'config-chip--on']"
            @click="toggleLayer(layer.type)"
          >
            <v-icon x-small class="config-chip__dot">{{ layerVisibility[layer.type] !== false ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
            {{ layer.label }}
          </button>
        </div>
      </div>
    </v-menu>

    <div class="toolbar-divider" />

    <!-- KPI -->
    <v-btn
      small
      :class="['toolbar-entity-btn', 'toolbar-entity-btn--inactive']"
      v-auto-tooltip="'KPIs'"
      @click="$emit('kpi-click')"
    >
      <v-icon small>mdi-chart-donut-variant</v-icon>
    </v-btn>
    </div>

  <!-- Right group: alertas activas + WS health + fullscreen + config + usuario -->
  <div class="toolbar-right-group">
    <!-- Alertas activas desglosadas: 911 / SOS / Alertas / Hits / Reportantes.
         Mismo look que los chips de recursos (icono con color de categoría
         + count blanco, bg neutro). SOS pulsea cuando hay llamada activa. -->
    <div class="toolbar-alerts">
      <button
        type="button"
        class="toolbar-stats__nav toolbar-stats__nav--prev"
        v-auto-tooltip="'Ver más'"
        @click="scrollChips('alertsScroll', -1)"
      >
        <v-icon x-small>mdi-chevron-left</v-icon>
      </button>
      <div ref="alertsScroll" class="toolbar-stats__scroll">
        <button
          v-for="chip in alertChips"
          :key="chip.key"
          type="button"
          class="toolbar-stat toolbar-stat--clickable"
          :class="{
            'toolbar-stat--dim': chip.count === 0,
            'toolbar-stat--urgent': chip.urgent && chip.count > 0,
          }"
          v-auto-tooltip="chip.label"
          @click="$emit('alerts-click', chip.key)"
        >
          <v-icon x-small class="toolbar-stat__icon" :color="chip.color">{{ chip.icon }}</v-icon>
          <span class="toolbar-stat__count">{{ chip.count }}</span>
        </button>
      </div>
      <button
        type="button"
        class="toolbar-stats__nav toolbar-stats__nav--next"
        v-auto-tooltip="'Ver más'"
        @click="scrollChips('alertsScroll', 1)"
      >
        <v-icon x-small>mdi-chevron-right</v-icon>
      </button>
    </div>

    <!-- WS health — estado del backend en tiempo real. Reemplaza al dot
         escondido en la esquina inferior izquierda (.ws-indicator). -->
    <span
      class="toolbar-ws"
      :class="wsConnected ? 'toolbar-ws--ok' : 'toolbar-ws--off'"
      :data-tip="wsConnected ? 'Conectado al backend' : 'Sin conexión'"
      data-tip-pos="bottom"
    >
      <span class="toolbar-ws__dot" />
      <span class="toolbar-ws__label">WS</span>
    </span>

    <div class="toolbar-divider" />

    <!-- Toggle del cintillo de incidentes (entre WS y maximizar) -->
    <v-btn
      v-if="capaOk('incident')"
      small
      :class="[
        'toolbar-entity-btn',
        tickerVisible ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
      ]"
      :color="tickerVisible ? 'var(--kln-accent)' : ''"
      :text="!tickerVisible"
      v-auto-tooltip="tickerVisible ? 'Ocultar cintillo de incidentes' : 'Mostrar cintillo de incidentes'"
      @click="$emit('toggle-ticker')"
    >
      <v-icon small>mdi-bullhorn-outline</v-icon>
    </v-btn>

    <v-btn
      v-if="verAnaliticos"
      small
      class="toolbar-entity-btn toolbar-entity-btn--inactive"
      text
      v-auto-tooltip="'Monitor de Analíticos (LPR)'"
      @click="openAnalytics"
    >
      <v-icon small>mdi-cctv</v-icon>
    </v-btn>

    <!-- Acceso desde el mapa al reporte "Usuarios por municipio" (C5).
         Visible para admins y cuentas con permission_user_stats: la cuenta
         de consulta vive en el mapa y no necesita entrar al backoffice. -->
    <v-btn
      v-if="canSeeUserStats"
      small
      class="toolbar-entity-btn toolbar-entity-btn--inactive"
      text
      v-auto-tooltip="'Usuarios por municipio'"
      @click="openUserStats"
    >
      <v-icon small>mdi-city</v-icon>
    </v-btn>

    <v-btn
      small
      class="toolbar-entity-btn toolbar-entity-btn--inactive"
      text
      v-auto-tooltip="'Pantalla completa'"
      @click="$emit('fullscreen')"
    >
      <v-icon small>mdi-fullscreen</v-icon>
    </v-btn>

    <v-menu
      v-model="configMenu"
      :close-on-content-click="false"
      offset-y
      bottom
      left
      :z-index="20"
    >
      <template #activator="{ on }">
        <v-btn
          small
          :class="[
            'toolbar-entity-btn',
            configMenu ? 'toolbar-entity-btn--active' : 'toolbar-entity-btn--inactive',
          ]"
          :color="configMenu ? 'var(--kln-accent)' : ''"
          :text="!configMenu"
          v-auto-tooltip="'Configuración'"
          v-on="on"
        >
          <v-icon small>mdi-cog</v-icon>
        </v-btn>
      </template>
      <div class="config-menu">
        <!-- Clustering -->
        <div class="config-menu__section">
          <div class="config-menu__title">Agrupación</div>
          <div class="config-menu__chips">
            <button
              type="button"
              :class="['config-chip', clusterEnabled && 'config-chip--on']"
              @click="onClusterEnabledChange(!clusterEnabled)"
            >
              <v-icon x-small class="config-chip__dot">{{ clusterEnabled ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Clustering
            </button>
          </div>
          <div v-if="clusterEnabled" class="config-menu__slider-label">
            Zoom máx: {{ clusterMaxZoom }}
          </div>
          <v-slider
            v-if="clusterEnabled"
            :value="clusterMaxZoom"
            min="8"
            max="18"
            step="1"
            dense
            hide-details
            thumb-label
            class="config-menu__slider"
            @change="onClusterMaxZoomChange"
          />
        </div>

        <!-- Jurisdicción (remarcado del EdoMex) -->
        <div class="config-menu__section">
          <div class="config-menu__title">Jurisdicción</div>
          <div class="config-menu__chips">
            <button
              type="button"
              :class="['config-chip', jurisdictionOutlineVisible && 'config-chip--on']"
              @click="onJurisdictionOutlineChange(!jurisdictionOutlineVisible)"
            >
              <v-icon x-small class="config-chip__dot">{{ jurisdictionOutlineVisible ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Remarcar EdoMex
            </button>
            <button
              type="button"
              :class="['config-chip', externalLabelsAttenuated && 'config-chip--on']"
              @click="onExternalLabelsAttenuatedChange(!externalLabelsAttenuated)"
            >
              <v-icon x-small class="config-chip__dot">{{ externalLabelsAttenuated ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Atenuar externos
            </button>
          </div>
        </div>

        <!-- Etiquetas de markers: modo de visualizacion -->
        <div class="config-menu__section">
          <div class="config-menu__title">Etiquetas de markers</div>
          <div class="config-menu__chips">
            <button
              type="button"
              :class="['config-chip', labelMode === 'dense' && 'config-chip--on']"
              v-auto-tooltip="'Todas las etiquetas siempre visibles (puede saturar)'"
              @click="onLabelModeChange('dense')"
            >
              <v-icon x-small class="config-chip__dot">{{ labelMode === 'dense' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Lleno
            </button>
            <button
              type="button"
              :class="['config-chip', labelMode === 'smart' && 'config-chip--on']"
              v-auto-tooltip="'Colisiones inteligentes; incidentes y SOS siempre visibles'"
              @click="onLabelModeChange('smart')"
            >
              <v-icon x-small class="config-chip__dot">{{ labelMode === 'smart' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Inteligente
            </button>
            <button
              type="button"
              :class="['config-chip', labelMode === 'minimal' && 'config-chip--on']"
              v-auto-tooltip="'Solo unidades en movimiento; incidentes/SOS siempre visibles'"
              @click="onLabelModeChange('minimal')"
            >
              <v-icon x-small class="config-chip__dot">{{ labelMode === 'minimal' ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Mínimo
            </button>
          </div>
        </div>

        <!-- Trail del reportante SOS -->
        <div class="config-menu__section">
          <div class="config-menu__title">Trail SOS</div>
          <div class="config-menu__chips">
            <button
              type="button"
              :class="['config-chip', sosTrailDenoise && 'config-chip--on']"
              @click="onSosTrailDenoiseChange(!sosTrailDenoise)"
            >
              <v-icon x-small class="config-chip__dot">{{ sosTrailDenoise ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              Filtrar ruido GPS
            </button>
          </div>
          <div v-if="sosTrailDenoise" class="config-menu__slider-label">
            Umbral mínimo: {{ sosTrailDenoiseMeters }} m
          </div>
          <v-slider
            v-if="sosTrailDenoise"
            :value="sosTrailDenoiseMeters"
            min="5"
            max="50"
            step="1"
            dense
            hide-details
            thumb-label
            class="config-menu__slider"
            @change="onSosTrailDenoiseMetersChange"
          />
        </div>

        <!-- Banners de alerta: que tipos disparan el toast rojo superior -->
        <div class="config-menu__section">
          <div class="config-menu__title-row">
            <div class="config-menu__title">Alertas en pantalla</div>
            <div class="config-menu__title-actions">
              <button
                type="button"
                class="config-menu__title-action"
                v-auto-tooltip="'Activar todas'"
                @click="onAllBanners(true)"
              >Todas</button>
              <button
                type="button"
                class="config-menu__title-action"
                v-auto-tooltip="'Desactivar todas'"
                @click="onAllBanners(false)"
              >Ninguna</button>
            </div>
          </div>
          <div class="config-menu__hint">
            Controla el toast flotante y su sonido. Las alertas siguen
            llegando al sidebar y al mapa.
          </div>
          <div class="config-menu__chips">
            <button
              v-for="t in bannerAlertOptions"
              :key="t.type"
              type="button"
              :class="['config-chip', bannerAlertTypes[t.type] && 'config-chip--on']"
              @click="onBannerAlertToggle(t.type, !bannerAlertTypes[t.type])"
            >
              <v-icon x-small class="config-chip__dot">{{ bannerAlertTypes[t.type] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              {{ t.label }}
            </button>
          </div>
        </div>

        <!-- Tema: chips radio-style (uno solo activo) -->
        <div v-if="showThemeSelector" class="config-menu__section">
          <div class="config-menu__title">Tema</div>
          <div class="config-menu__chips">
            <button
              v-for="t in availableThemes"
              :key="t.id"
              type="button"
              :class="['config-chip', t.id === currentThemeId && 'config-chip--on']"
              @click="changeTheme(t.id)"
            >
              <v-icon x-small class="config-chip__dot">{{ t.id === currentThemeId ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              {{ t.label }}
            </button>
          </div>
        </div>

        <!-- Logo Murphy: sobrio (grises) vs tematizado (al accent del tema) -->
        <div class="config-menu__section">
          <div class="config-menu__title">Logo Murphy</div>
          <div class="config-menu__chips">
            <button
              v-for="m in murphyModeOptions"
              :key="m.id"
              type="button"
              :class="['config-chip', m.id === currentMurphyMode && 'config-chip--on']"
              @click="changeMurphyMode(m.id)"
            >
              <v-icon x-small class="config-chip__dot">{{ m.id === currentMurphyMode ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
              {{ m.label }}
            </button>
          </div>
        </div>
      </div>
    </v-menu>

    <account-menu />
  </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import Dialog from '@/components/dialog'
import { ENTITY_CONFIGS } from '../data/entity-configs.js'
import { capaPermitida, capaDeAlerta, categoriaVisible, visibilidadEfectiva, filtrosGeocercaEfectivos } from '../data/layer-permissions.js'
import { isCameraOwnershipEnabled } from '../data/camera-ownership.js'
import { navSections } from '@/router/nav-model'
import Weather from '@/components/map/leftPanel/Weather.vue'
import AppLauncher from '@/components/nav/AppLauncher.vue'
import AccountMenu from '@/components/nav/AccountMenu.vue'
import { getEnabledThemes, getTheme } from '@/themes'
import {
  persistAndReload,
  readPersistedTheme,
  THEME_CHANGE_CONFIRM,
  persistMurphyModeAndReload,
  readMurphyMode,
  MURPHY_MODE_CHANGE_CONFIRM,
} from '@/themes/apply'

const MURPHY_MODE_OPTIONS = [
  { id: 'neutral', label: 'Sobrio (grises)' },
  { id: 'themed',  label: 'Tematizado'    },
]

export default {
  name: 'MapToolbar',

  components: { Weather, AppLauncher, AccountMenu },

  props: {
    groupsByEntity: { type: Object, default: function() { return {} } },
    user: { type: Object, default: null },
    // Conteo live de monitores por entityType. Se muestra junto al reloj.
    // Formato: { incident, device, bus, lpr, sensor }. MapV2 lo recalcula
    // en cada sidebarTick desde _monitors.
    entityStats: { type: Object, default: function() { return {} } },
    // Conteo de alertas activas desglosado por categoría:
    // { '911', 'sos', 'event', 'hit', 'reportant' }. Se renderiza en el
    // right-group como 5 chips compactos; click en cualquiera abre sidebar.
    activeAlertCounts: {
      type: Object,
      default: function() { return { '911': 0, sos: 0, event: 0, hit: 0, reportant: 0 } },
    },
    // Estado de la conexión al backend (WS). Dot rojo/verde en el right-group.
    wsConnected: { type: Boolean, default: false },
    // Cintillo de incidentes visible — controla el estado activo del toggle.
    tickerVisible: { type: Boolean, default: false },
  },

  data() {
    return {
      dateStr: '',
      timeStr: '',
      currentThemeId: readPersistedTheme(),
      currentMurphyMode: readMurphyMode(),
      murphyModeOptions: MURPHY_MODE_OPTIONS,
      configMenu: false,
      filtersMenu: false,
      filterExpanded: { incident: false, camera: false, bus: false, lpr: false, device: false, sensor: false },
      incidentCategoryOptions: [
        { value: 'proteccion_civil', label: 'P. Civil' },
        { value: 'servicios_publicos', label: 'Serv. Púb.' },
        { value: 'medico', label: 'Médico' },
        { value: 'seguridad', label: 'Seguridad' },
      ],
      filterSections: [
        { type: 'incident', label: 'Incidentes' },
        { type: 'camera', label: 'Cámaras' },
        { type: 'bus', label: 'Transporte' },
        { type: 'lpr', label: 'Analíticos' },
        { type: 'device', label: 'Unidades' },
        { type: 'sensor', label: 'Botones de pánico' },
        { type: 'external_device', label: 'Unidades externas' },
      ],
      // Ámbito de cámaras — el color es el mismo de la placa del marker
      // (inyectado por applyMarkerPalette desde la paleta del tema activo).
      ownershipEnabled: isCameraOwnershipEnabled(),
      ownershipOptions: [
        { value: 'estatal', label: 'Estatales', color: 'var(--kln-marker-camara-estatal)' },
        { value: 'municipal', label: 'Municipales', color: 'var(--kln-marker-camara-municipal)' },
        { value: 'otro', label: 'Otras', color: 'var(--kln-marker-camara-otro)' },
      ],
      statusOptions: {
        camera: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }],
        bus: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }, { value: 'alert', label: 'Alerta' }],
        lpr: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }],
        device: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }],
        sensor: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }, { value: 'alert', label: 'Alerta' }],
        // Los 4 estados que su getStatus puede devolver. 'Reportado' se expone
        // como chip propio para que el operador pueda ocultar las unidades ya
        // despachadas sin perder de vista las que siguen en alerta.
        external_device: [{ value: 'online', label: 'En línea' }, { value: 'offline', label: 'Fuera de línea' }, { value: 'alert', label: 'Alerta' }, { value: 'reported', label: 'Reportado' }],
      },
      // Primary: direct toggle buttons
      primaryLayers: ['device', 'bus', 'external_device', 'camera', 'lpr', 'sensor', 'incident'].map(type => {
        const cfg = ENTITY_CONFIGS[type]
        return {
          type, label: cfg.label, icon: cfg.mdiIcon, color: cfg.color,
        }
      }),
      // Submenu: Ambiente
      ambienteLayers: ['presa', 'sismo', 'fire'].map(type => {
        const cfg = ENTITY_CONFIGS[type]
        return {
          type, label: cfg.label, icon: cfg.mdiIcon, color: cfg.color,
        }
      }),
    }
  },

  created() {
    this._updateDatetime()
    this._datetimeInterval = setInterval(this._updateDatetime, 1000)
  },

  beforeDestroy() {
    if (this._datetimeInterval) clearInterval(this._datetimeInterval)
  },

  computed: {
    ...mapState('map-v2', { layerVisibilityGuardada: 'layerVisibility' }),

    ...mapState('map-v2', [
      'viewport',
      'heatmapActive',
      'clusterEnabled',
      'clusterMaxZoom',
      'entityFilters',
      'sosTrailDenoise',
      'sosTrailDenoiseMeters',
      'jurisdictionOutlineVisible',
      'externalLabelsAttenuated',
      'labelMode',
      'bannerAlertTypes',
      'geofenceFilters',
    ]),

    ...mapGetters('auth', ['getUser']),

    // Acceso al reporte "Usuarios por municipio": admins y cuentas de
    // consulta de C5 (attributes.permission_user_stats). Mismo criterio que
    // el meta.validate de la ruta /users-by-municipio.
    // Reportantes (RAPID-SOS): el payload trae el telefono de quien llamo al
    // 911. Sin permiso no se pide ni se ingiere (MapV2), y aqui ademas se
    // quitan el toggle de capa y el chip de conteo para no anunciar un
    // modulo que el operador no tiene.
    // Capas permitidas por el perfil (Parte D). Con el flag apagado todo es true.
    capas() {
      return this.$store.getters['accesos/capasPermitidas']
    },

    // Filtros de geocercas con la máscara del perfil: un nivel oculto no enciende nada.
    geofenceFiltersEfectivos() {
      return filtrosGeocercaEfectivos(this.geofenceFilters, this.capas)
    },

    geofencesVisible() {
      var f = this.geofenceFiltersEfectivos
      return !!(f.geofence || f.region || f.sector || f.cuadrante)
    },

    layerVisibility() {
      return visibilidadEfectiva(this.layerVisibilityGuardada, this.capas)
    },

    filterSections() {
      return [
        { type: 'incident', label: 'Incidentes' },
        { type: 'camera', label: 'Cámaras' },
        { type: 'bus', label: 'Transporte' },
        { type: 'lpr', label: 'Analíticos' },
        { type: 'device', label: 'Unidades' },
        { type: 'sensor', label: 'Botones de pánico' },
      ].filter(s => capaPermitida(this.capas, s.type))
    },

    // Primary: direct toggle buttons
    primaryLayers() {
      return ['device', 'bus', 'camera', 'lpr', 'sensor', 'incident']
        .filter(type => capaPermitida(this.capas, type))
        .map(type => {
          const cfg = ENTITY_CONFIGS[type]
          return {
            type, label: cfg.label, icon: cfg.mdiIcon, color: cfg.color,
          }
        })
    },

    // Submenu: Ambiente
    ambienteLayers() {
      return ['presa', 'sismo', 'fire']
        .filter(type => capaPermitida(this.capas, type))
        .map(type => {
          const cfg = ENTITY_CONFIGS[type]
          return {
            type, label: cfg.label, icon: cfg.mdiIcon, color: cfg.color,
          }
        })
    },

    // Submenu: Otros. Computed (no data) para que el gate de reportantes
    // aplique tambien a "activar/desactivar todos" y a hasActiveOtros.
    otrosLayers() {
      var types = ['cpr', 'external_sensor', 'reportant', 'sos_call', 'route_lpr']
      types = types.filter(t => capaPermitida(this.capas, t))
      return types.map(type => {
        const cfg = ENTITY_CONFIGS[type]
        return {
          type, label: cfg.label, icon: cfg.mdiIcon, color: cfg.color,
        }
      })
    },

    canSeeUserStats() {
      const user = this.getUser
      if (!user) return false
      return user.administrator === true
        || (user.attributes && user.attributes.permission_user_stats === true)
    },

    // Gate del botón de Analíticos con la misma fuente de verdad que el
    // guard (`canAccess` vía `navSections`). Sin esto, un operador sin
    // `permission_analytics_monitor` podía pulsarlo, disparar el leave
    // guard de MapV2 (que puede finalizar una llamada SOS activa) y solo
    // entonces enterarse de que el guard lo iba a rebotar — perdiendo la
    // llamada a cambio de nada.
    verAnaliticos() {
      return navSections(this.user).some((s) => s.id === 'analytics')
    },

    // Lista ordenada y etiquetada de las 5 estadísticas que se muestran
    // junto al reloj. Orden pedido por el usuario: Incidentes, GPS,
    // Transporte Público, Analíticos (LPR), Botones de pánico. El color
    // se lee de ENTITY_CONFIGS para empatar con los toggles del toolbar.
    statChips() {
      var s = this.entityStats || {}
      return [
        { type: 'incident', label: 'Incidentes',         icon: ENTITY_CONFIGS.incident.mdiIcon, color: ENTITY_CONFIGS.incident.color, count: s.incident || 0 },
        { type: 'device',   label: 'GPS',                icon: ENTITY_CONFIGS.device.mdiIcon,   color: ENTITY_CONFIGS.device.color,   count: s.device   || 0 },
        { type: 'bus',      label: 'Transporte Público', icon: ENTITY_CONFIGS.bus.mdiIcon,      color: ENTITY_CONFIGS.bus.color,      count: s.bus      || 0 },
        { type: 'lpr',      label: 'Analíticos',         icon: ENTITY_CONFIGS.lpr.mdiIcon,      color: ENTITY_CONFIGS.lpr.color,      count: s.lpr      || 0 },
        { type: 'sensor',   label: 'Botones de pánico',  icon: ENTITY_CONFIGS.sensor.mdiIcon,   color: ENTITY_CONFIGS.sensor.color,   count: s.sensor   || 0 },
      ].filter(chip => capaPermitida(this.capas, chip.type))
    },

    // Chips de alertas desglosados por categoría. Colores deliberadamente
    // DISTINTOS entre sí (cyan / rojo / amber / violeta / verde) para que
    // el operador los diferencie de un vistazo. Sincronizado con el array
    // `alertCategories` del MapSidebar. SOS urgente: latido rojo.
    alertChips() {
      var c = this.activeAlertCounts || {}
      var chips = [
        { key: '911',       label: '9-1-1',       icon: 'mdi-pulse',           color: 'var(--kln-accent)',        count: c['911']    || 0 },
        { key: 'sos',       label: 'SOS',         icon: 'mdi-phone-in-talk',   color: 'var(--kln-alert-danger)',  count: c.sos       || 0, urgent: true },
        { key: 'event',     label: 'Alertas',     icon: 'mdi-alarm-light',     color: 'var(--kln-alert-warning)', count: c.event     || 0 },
        { key: 'hit',       label: 'Hits',        icon: 'mdi-camera-front',    color: 'var(--kln-entity-violet)', count: c.hit       || 0 },
        { key: 'reportant', label: 'Reportantes', icon: 'mdi-account-alert',   color: 'var(--kln-entity-green)',  count: c.reportant || 0 },
      ]
      return chips.filter(x => categoriaVisible(x.key, this.capas))
    },

    hasActiveEntityFilters() {
      var ef = this.entityFilters || {}
      var types = Object.keys(ef)
      for (var i = 0; i < types.length; i++) {
        var f = ef[types[i]]
        if (!f) continue
        // Cualquier groupId seleccionado = filtro activo
        if (f.groupId && f.groupId.length > 0) return true
        // Status con menos de 2 opciones = filtro activo
        if (f.status && f.status.length > 0 && f.status.length < 3) return true
        // Category (incidentes) con menos de todas = filtro activo
        if (f.category && f.category.length > 0 && f.category.length < 7) return true
        // Ámbito de cámaras con menos de los 3 = filtro activo
        if (f.ownership && f.ownership.length > 0 && f.ownership.length < 3) return true
      }
      return false
    },

    styleId() {
      return this.viewport.styleId || 'dark'
    },

    // True si el tema activo es claro (diurno). Acota qué basemaps ofrece
    // el toggle: tema claro → [Día, Satélite]; temas oscuros → [Oscuro,
    // Satélite, Calles] (sin cambios). La reconciliación del styleId con el
    // tema vive en MapV2.created().
    isLightTheme() {
      var theme = getTheme(this.currentThemeId)
      return !!(theme && theme.isLight)
    },

    hasActiveAmbiente() {
      return this.ambienteLayers.some(function(l) {
        return this.layerVisibility[l.type] !== false
      }.bind(this))
    },

    hasActiveOtros() {
      return this.otrosLayers.some(function(l) {
        return this.layerVisibility[l.type] !== false
      }.bind(this))
    },

    availableThemes() {
      return getEnabledThemes()
    },

    showThemeSelector() {
      return this.availableThemes.length > 1
    },

    // Labels para los toggles de banners de alerta. Orden por criticidad
    // operativa (SOS + 9-1-1 primero). Los tipos estan hardcoded para
    // coincidir 1:1 con las keys de state.bannerAlertTypes; si se agrega
    // uno nuevo hay que actualizar tambien el default del store.
    bannerAlertOptions() {
      return [
        { type: 'sos_call',              label: 'Llamadas SOS' },
        { type: 'incident',              label: 'Incidentes 9-1-1' },
        { type: 'sensor_alert',          label: 'Pánico (sensor físico)' },
        { type: 'external_sensor_alert', label: 'Pánico (sensor externo)' },
        { type: 'bus_alert',             label: 'Pánico (autobús)' },
        { type: 'external_device_alert', label: 'Pánico (unidad externa)' },
        { type: 'device_alert',          label: 'Pánico (patrulla)' },
        { type: 'mc_alert',              label: 'Medidas cautelares' },
      ].filter(o => capaPermitida(this.capas, capaDeAlerta(o.type)))
    },

    // Los 4 niveles independientes del menu de Geocercas. Orden: primero
    // las generales, luego los 3 niveles del mando unico de mayor a menor
    // granularidad (paridad con legacy MapActions.vue §Mando Unico).
    geofenceFilterOptions() {
      return [
        { type: 'geofence',  label: 'Geocercas' },
        { type: 'region',    label: 'Regiones' },
        { type: 'sector',    label: 'Sectores' },
        { type: 'cuadrante', label: 'Cuadrantes' },
      ].filter(o => capaPermitida(this.capas, o.type))
    },
  },

  methods: {
    capaOk(tipo) {
      return capaPermitida(this.capas, tipo)
    },

    // Navegación ENTRE secciones: misma pestaña. La consulta contextual sobre
    // un item concreto (p.ej. el reporte de una unidad desde el drawer) sí abre
    // pestaña nueva, y va señalada con mdi-open-in-new.
    openAnalytics() {
      this.$router.push({ name: 'analytics-monitor' }).catch(() => {})
    },

    // Abre el reporte "Usuarios por municipio" como diálogo sobre el mapa
    // (Dialog.appendPage, mismo patrón que users.service.groups). Import
    // lazy: la vista no entra al bundle del mapa si nunca se abre. Buscador
    // y refresh viven en el HEADER del diálogo (junto a la X) para
    // aprovechar esa barra; la página embedded oculta su toolbar propio.
    async openUserStats() {
      const [mod, vuetifyLib, vuetifyPlugin] = await Promise.all([
        import('@/views/UsersByMunicipio.vue'),
        import('vuetify/lib'),
        import('@/plugins/vuetify'),
      ])
      const Elem = Vue.extend(mod.default)
      const { VTextField } = vuetifyLib
      // Instancia del PLUGIN vuetify (la misma que usa el Dialog) — pasar
      // this.$vuetify (el framework) truena con "vuetify.init is not a function".
      const vuetify = vuetifyPlugin.default
      let page = null
      const dialog = new Dialog({
        title: 'Usuarios por municipio',
        size: 'x-large',
        height: '800',
        actions: [{
          icon: 'mdi-refresh',
          help: 'Actualizar',
          action: () => { if (page) page.load() },
        }],
      })
      dialog.open()
      page = dialog.appendPage(Elem, { embedded: true })
      // Buscador inyectado en la barra del header, antes de los botones.
      const search = new Vue({
        vuetify,
        render(h) {
          return h(VTextField, {
            staticClass: 'muni-header-search',
            props: {
              dark: true,
              dense: true,
              flat: true,
              solo: true,
              hideDetails: true,
              clearable: true,
              placeholder: 'Buscar municipio',
              prependInnerIcon: 'mdi-magnify',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
            style: { width: '260px', marginRight: '12px' },
            on: { input: (value) => { if (page) page.search = value || '' } },
          })
        },
      })
      search.$mount()
      dialog.addAction(search.$el, true)
    },

    _updateDatetime() {
      const now = new Date()
      const dd = String(now.getDate()).padStart(2, '0')
      const mm = String(now.getMonth() + 1).padStart(2, '0')
      const yyyy = now.getFullYear()
      const hh = String(now.getHours()).padStart(2, '0')
      const mi = String(now.getMinutes()).padStart(2, '0')
      const ss = String(now.getSeconds()).padStart(2, '0')
      this.dateStr = `${dd}/${mm}/${yyyy}`
      this.timeStr = `${hh}:${mi}:${ss} hrs.`
    },

    ...mapMutations('map-v2', [
      'SET_VIEWPORT',
      'SET_LAYER_VISIBILITY',
      'SET_HEATMAP_ACTIVE',
      'SET_CLUSTER_SETTINGS',
      'SET_ENTITY_FILTER',
      'SET_SOS_TRAIL_DENOISE',
      'SET_JURISDICTION_OUTLINE_VISIBLE',
      'SET_EXTERNAL_LABELS_ATTENUATED',
      'SET_LABEL_MODE',
      'SET_BANNER_ALERT_TYPE',
      'SET_ALL_BANNER_ALERT_TYPES',
      'SET_GEOFENCE_FILTER',
      'SET_ALL_GEOFENCE_FILTERS',
    ]),

    onGeofenceFilterToggle(type, value) {
      this.SET_GEOFENCE_FILTER({ type: type, enabled: !!value })
    },

    onAllGeofenceFilters(enabled) {
      this.SET_ALL_GEOFENCE_FILTERS(!!enabled)
    },

    isFilterActive(entityType, field, value) {
      var ef = this.entityFilters || {}
      var arr = ef[entityType] && ef[entityType][field]
      if (!arr) return false
      return arr.indexOf(value) !== -1
    },

    toggleFilter(entityType, field, value) {
      var ef = this.entityFilters || {}
      var current = (ef[entityType] && ef[entityType][field]) || []
      var idx = current.indexOf(value)
      var updated = current.slice()
      if (idx === -1) {
        updated.push(value)
      } else {
        updated.splice(idx, 1)
      }
      this.SET_ENTITY_FILTER({ entityType: entityType, filters: { [field]: updated } })
    },

    // Ámbito de cámaras. A diferencia de status/category, el filtro no
    // existe en localStorage de usuarios previos a este feature: ausencia
    // de array = los 3 ámbitos visibles, y el primer toggle lo materializa
    // partiendo de los 3 completos.
    isOwnershipActive(value) {
      var ef = this.entityFilters || {}
      var arr = ef.camera && ef.camera.ownership
      if (!arr || !arr.length) return true
      return arr.indexOf(value) !== -1
    },

    toggleOwnership(value) {
      var ef = this.entityFilters || {}
      var current = (ef.camera && ef.camera.ownership) || []
      if (!current.length) current = this.ownershipOptions.map(function(o) { return o.value })
      var updated = current.slice()
      var idx = updated.indexOf(value)
      if (idx === -1) {
        updated.push(value)
      } else {
        updated.splice(idx, 1)
      }
      this.SET_ENTITY_FILTER({ entityType: 'camera', filters: { ownership: updated } })
    },

    onStyleChange(styleId) {
      if (!styleId) return
      this.SET_VIEWPORT({ styleId })
      this.$emit('style-change', styleId)
    },

    // Scroll horizontal del carrusel de chips. dir = -1 (anterior) | 1 (siguiente).
    // Solo aplica en viewports angostos donde el contenedor está limitado por
    // CSS — en pantallas grandes el contenedor no overflowea y el scroll
    // es un no-op silencioso. El paso es 2 chips (~140 px) para que cada
    // clic revele aprox. el doble del ancho visible.
    scrollChips(ref, dir) {
      var el = this.$refs[ref]
      if (!el) return
      el.scrollBy({ left: dir * 140, behavior: 'smooth' })
    },

    toggleLayer(entityType) {
      const current = this.layerVisibility[entityType] !== false
      this.SET_LAYER_VISIBILITY({ entityType, visible: !current })
      this.$emit('layer-toggle', { entityType, visible: !current })
    },

    // Bulk: aplica on/off a cada layer de la lista. Emitimos uno por uno
    // para que la sincronizacion con el mapa (MapV2 onLayerToggle) corra
    // su rebuild incremental por entidad en vez de un gran setData global.
    onAllLayerToggle(layers, enabled) {
      if (!Array.isArray(layers)) return
      var want = !!enabled
      for (var i = 0; i < layers.length; i++) {
        var t = layers[i].type
        var current = this.layerVisibility[t] !== false
        if (current === want) continue
        this.SET_LAYER_VISIBILITY({ entityType: t, visible: want })
        this.$emit('layer-toggle', { entityType: t, visible: want })
      }
    },

    toggleHeatmap() {
      // Capturamos el NUEVO valor antes de mutar el store. Vuex es síncrono:
      // sin esto, el segundo `this.heatmapActive` lee ya el nuevo valor y
      // emitimos el contrario (bug que hacía ver el toggle inconsistente).
      var next = !this.heatmapActive
      this.SET_HEATMAP_ACTIVE(next)
      this.$emit('heatmap-toggle', next)
    },

    onClusterEnabledChange(value) {
      this.SET_CLUSTER_SETTINGS({ enabled: value })
      this.$emit('cluster-change', { enabled: value, maxZoom: this.clusterMaxZoom })
    },

    onClusterMaxZoomChange(value) {
      this.SET_CLUSTER_SETTINGS({ maxZoom: value })
      this.$emit('cluster-change', { enabled: this.clusterEnabled, maxZoom: value })
    },

    onSosTrailDenoiseChange(value) {
      this.SET_SOS_TRAIL_DENOISE({ enabled: value })
    },

    onSosTrailDenoiseMetersChange(value) {
      this.SET_SOS_TRAIL_DENOISE({ meters: value })
    },

    onJurisdictionOutlineChange(value) {
      this.SET_JURISDICTION_OUTLINE_VISIBLE(!!value)
    },

    onExternalLabelsAttenuatedChange(value) {
      this.SET_EXTERNAL_LABELS_ATTENUATED(!!value)
    },

    onLabelModeChange(mode) {
      this.SET_LABEL_MODE(mode)
    },

    onBannerAlertToggle(type, value) {
      this.SET_BANNER_ALERT_TYPE({ type: type, enabled: !!value })
    },

    onAllBanners(enabled) {
      this.SET_ALL_BANNER_ALERT_TYPES(!!enabled)
    },

    async changeTheme(themeId) {
      if (themeId === this.currentThemeId) return
      try {
        await this.$confirm(THEME_CHANGE_CONFIRM)
        persistAndReload(themeId)
      } catch (_) {
        // usuario cancelo; no hacemos nada, la UI se queda en el tema actual
      }
    },

    async changeMurphyMode(mode) {
      if (mode === this.currentMurphyMode) return
      try {
        await this.$confirm(MURPHY_MODE_CHANGE_CONFIRM)
        persistMurphyModeAndReload(mode)
      } catch (_) {
        // usuario cancelo; UI mantiene el modo actual
      }
    },
  },
}
</script>

<style scoped>
/* ── Toolbar container ── */
.map-toolbar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  min-height: var(--kln-topbar-height, 48px);
  background: var(--kln-bg-surface);
  /* Solo regla inferior, como AppSectionHeader. El borde en los cuatro lados
     dibujaba una caja pegada a los bordes de la ventana: la barra va a ras,
     sin margen, así que los laterales y el superior caían sobre el filo. */
  border-bottom: 1px solid var(--kln-border-dim);
  width: 100%;
}

.map-toolbar > .toolbar-datetime {
  justify-self: start;
}

.map-toolbar > .toolbar-right-group {
  justify-self: end;
}

.toolbar-right-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-center-group {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.toolbar-datetime {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Geist Mono', 'JetBrains Mono', 'Menlo', monospace;
  font-size: 13px;
  color: var(--kln-text);
  white-space: nowrap;
  letter-spacing: 0.02em;
}

.toolbar-datetime >>> .weather {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toolbar-datetime >>> .weather-img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  /* Recolorea el PNG de openweathermap al accent del tema via hue-rotate.
     Default (cyan #00D4FF, vibrante): 150deg.
     Override por [data-theme] abajo para temas con otro accent. */
  filter: brightness(0) saturate(100%) invert(76%) sepia(58%) saturate(3893%) hue-rotate(150deg) brightness(102%) contrast(101%);
}

[data-theme="desaturado"] .toolbar-datetime >>> .weather-img {
  /* Rotación adicional hacia lavanda/violeta para combinar con accent #B4A9FF */
  filter: brightness(0) saturate(100%) invert(76%) sepia(58%) saturate(3893%) hue-rotate(240deg) brightness(102%) contrast(101%);
}

[data-theme="diurno"] .toolbar-datetime >>> .weather-img {
  /* Guinda #9f2241 (accent del tema claro): más oscuro (invert/brightness
     bajos) y rotado hacia el rojo. Aproximado vía el hack de filtros sobre
     el PNG de openweathermap; ajustable con hue-rotate/brightness si el tono
     no calza exactamente. */
  filter: brightness(0) saturate(100%) invert(28%) sepia(60%) saturate(3500%) hue-rotate(313deg) brightness(88%) contrast(100%);
}

.toolbar-datetime >>> .temp {
  color: var(--kln-accent);
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
}

.toolbar-clock {
  display: flex;
  flex-direction: column;
  font-family: inherit;
  line-height: 1.1;
  letter-spacing: 0.04em;
}

.toolbar-clock__date {
  font-size: 11px;
  font-weight: 500;
  color: var(--kln-text-muted);
}

.toolbar-clock__time {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text);
  font-variant-numeric: tabular-nums;
}

/* ── Stats chips (junto al reloj) ── */
.toolbar-stats {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding-left: 8px;
  border-left: 1px solid var(--kln-border-dim);
}

.toolbar-stat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 8px;
  background: var(--kln-border-dim);
  border: 1px solid var(--kln-border-dim);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text);
  letter-spacing: 0;
  cursor: default;
  transition: background 0.15s;
}

.toolbar-stat:hover {
  background: var(--kln-border-dim);
}

.toolbar-stat__icon {
  font-size: 14px !important;
}

.toolbar-stat__count {
  font-variant-numeric: tabular-nums;
}

/* ── Alertas activas por categoría (right group) ── */
/* Reusa .toolbar-stat (mismo look que los chips de recursos). Variantes:
   - --clickable: cursor pointer para los del right-group.
   - --dim: count === 0 (atenuado).
   - --urgent: SOS con count > 0 (latido rojo). */
.toolbar-alerts {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* Como se usa sobre <button>, hay que resetear estilos nativos para que
   luzca igual que los <span> de los chips de recursos. */
button.toolbar-stat,
.toolbar-stat--clickable {
  cursor: pointer !important;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
}

.toolbar-stat--clickable:hover {
  transform: translateY(-1px);
}

.toolbar-stat--dim {
  opacity: 0.4;
}

.toolbar-stat--urgent {
  animation: toolbar-alerts-pulse 1.6s ease-in-out infinite;
}

@keyframes toolbar-alerts-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.45); }
  50%      { box-shadow: 0 0 0 5px rgba(231, 76, 60, 0); }
}

/* ── WS health ── */
.toolbar-ws {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--kln-text-dim);
  cursor: default;
  user-select: none;
}

.toolbar-ws__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  transition: background 0.3s, box-shadow 0.3s;
}

.toolbar-ws--ok .toolbar-ws__dot {
  background: #1E9553;
  box-shadow: 0 0 6px rgba(30, 149, 83, 0.6);
}

.toolbar-ws--off .toolbar-ws__dot {
  background: #FF4C51;
  box-shadow: 0 0 6px rgba(255, 76, 81, 0.6);
  animation: toolbar-ws-blink 1s ease-in-out infinite;
}

.toolbar-ws--off .toolbar-ws__label {
  color: rgba(255, 76, 81, 0.85);
}

@keyframes toolbar-ws-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}

/* ── Section groups ── */
.toolbar-group {
  display: flex;
  align-items: center;
  gap: 2px;
  background: transparent !important;
}

/* ── Submenu dropdown ── */
.toolbar-submenu {
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 200px;
}

/* Variante de submenu con chips: narrow + column. Los chips se apilan
   verticalmente (uno por linea, full-width) para que el dropdown sea mas
   alto que ancho. Aplica a Geocercas / Ambiente / Otros elementos. La
   seccion "Alertas en pantalla" del menu de configuracion principal si
   usa flex-wrap horizontal (scope distinto). */
.toolbar-submenu--wide {
  width: 220px;
  max-width: 220px;
  box-sizing: border-box;
}

.toolbar-submenu--wide .config-menu__chips {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.toolbar-submenu--wide .config-chip {
  justify-content: flex-start;
  width: 100%;
}

.toolbar-submenu__title {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
}


/* ── Vertical divider between sections ── */
.toolbar-divider {
  width: 1px;
  height: 24px;
  background: var(--kln-border-normal);
  margin: 0 4px;
  flex-shrink: 0;
}

/* ── Style toggle (VBtnToggle) ── */
.toolbar-style-toggle.v-btn-toggle {
  background: transparent !important;
}

.toolbar-style-btn {
  min-width: 0 !important;
  padding: 0 6px !important;
  color: var(--kln-text-dim) !important;
  background: transparent !important;
  transition: color 0.2s, background-color 0.2s;
}

.toolbar-style-btn.v-btn--active {
  color: #fff !important;
  background: var(--kln-border-accent) !important;
}

.toolbar-style-btn:hover {
  color: var(--kln-text) !important;
}

/* ── Entity toggle buttons (the KEY change) ── */
.toolbar-entity-btn {
  min-width: 0 !important;
  padding: 0 6px !important;
  border-radius: 6px !important;
  transition: color 0.2s, background-color 0.2s, opacity 0.2s;
}

/* Active: entity color as background, white icon */
.toolbar-entity-btn--active {
  color: #fff !important;
}

/* Inactive: transparent bg, muted icon */
.toolbar-entity-btn--inactive {
  color: var(--kln-text-dim) !important;
  background: transparent !important;
}

.toolbar-entity-btn--inactive:hover {
  color: var(--kln-text-muted) !important;
  background: var(--kln-border-dim) !important;
}

/* ── Config menu ── */
/* ── Filters panel ── */
.filters-panel {
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  padding: 8px 0;
  min-width: 260px;
  max-width: 300px;
}
.filters-panel__title {
  font-size: 10px;
  font-weight: 600;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 4px 14px 8px;
}
.filter-section {
  border-top: 1px solid var(--kln-border-dim);
}
.filter-section__header {
  display: flex;
  align-items: center;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--kln-text-muted);
  cursor: pointer;
  user-select: none;
}
.filter-section__header:hover {
  background: var(--kln-border-dim);
}
.filter-section__body {
  padding: 2px 14px 8px;
}
.filter-label {
  font-size: 10px;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  margin: 4px 0 2px;
}
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
/* Leyenda del color de la placa de ámbito de cámaras */
.ownership-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  flex: none;
}
.filter-chips .v-chip {
  font-size: 10px !important;
  height: 22px !important;
  cursor: pointer;
}
.filter-group-select {
  margin-top: 2px;
  font-size: 11px;
}
.filter-group-select >>> .v-input__slot {
  min-height: 28px !important;
  padding: 0 8px !important;
}
.filter-group-select >>> input,
.filter-group-select >>> .v-select__selection {
  font-size: 11px !important;
}
.filter-group-select >>> .v-select__selection {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.config-menu {
  background: var(--kln-bg-overlay);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  padding: 8px 14px 12px;
  /* Ancho fijo para que los chips hagan wrap a nuevas lineas en vez de
     estirar el menu horizontalmente. Si los titulos uppercase caben (y
     caben: el mas largo, "ALERTAS EN PANTALLA", mide ~160px), 340px deja
     espacio comodo para 2-3 chips por linea con wrap natural. */
  width: 340px;
  max-width: 340px;
  box-sizing: border-box;
}

.config-menu__section {
  padding: 6px 0;
}

.config-menu__section + .config-menu__section {
  border-top: 1px solid var(--kln-border-dim);
}

.config-menu__title {
  font-size: 10px;
  font-weight: 600;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.config-menu__title-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
}

.config-menu__title-row .config-menu__title,
.config-menu__title-row .toolbar-submenu__title {
  margin-bottom: 0;
}

.config-menu__title-actions {
  display: flex;
  gap: 4px;
}

.config-menu__title-action {
  background: transparent;
  border: 1px solid var(--kln-border-dim);
  border-radius: 4px;
  color: var(--kln-text-muted);
  font-size: 10px;
  padding: 1px 7px;
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.config-menu__title-action:hover {
  color: var(--kln-text);
  border-color: var(--kln-border-normal);
  background: var(--kln-border-dim);
}

.config-menu__hint {
  font-size: 10px;
  color: var(--kln-text-dim);
  line-height: 1.4;
  margin-bottom: 6px;
}

/* ── Chip toggle (lenguaje compartido por todas las secciones) ── */
.config-menu__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
}

.config-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px 4px 6px;
  background: transparent;
  border: 1px solid var(--kln-border-normal);
  border-radius: 999px;
  color: var(--kln-text-dim);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.3;
  cursor: pointer;
  user-select: none;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.config-chip:hover {
  color: var(--kln-text-muted);
  border-color: var(--kln-border-normal);
  background: var(--kln-border-dim);
}

.config-chip__dot {
  opacity: 0.55;
}

.config-chip--on {
  color: #fff;
  border-color: var(--kln-accent);
  background: color-mix(in srgb, var(--kln-accent) 18%, transparent);
}

.config-chip--on .config-chip__dot {
  color: var(--kln-accent) !important;
  opacity: 1;
}

.config-chip--on:hover {
  background: color-mix(in srgb, var(--kln-accent) 26%, transparent);
  border-color: var(--kln-accent);
}

.config-menu__slider-label {
  font-size: 11px;
  color: var(--kln-text-dim);
  margin-bottom: 2px;
  margin-top: 6px;
}

.config-menu__slider {
  margin-top: 0;
}

/* ── User menu ── */
/* ── Carrusel de chips (stats izq + alerts der) ──
   El layout flex de `.toolbar-stats` y `.toolbar-alerts` ya está definido
   arriba (línea ~1124 y ~1166). Aquí solo agregamos el contenedor scroll
   interno y las flechas de navegación. En viewport amplio las flechas
   quedan ocultas (display: none) y el scroll se expande sin overflowear;
   en viewport angosto se acota el scroll a ~2 chips y las flechas
   aparecen. scrollChips() en JS hace scrollBy. */
.toolbar-stats__scroll {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  /* Ocultar scrollbar nativa — la navegación es con las flechas */
  scrollbar-width: none;
}
.toolbar-stats__scroll::-webkit-scrollbar { display: none; }
.toolbar-stats__scroll > .toolbar-stat {
  scroll-snap-align: start;
  flex-shrink: 0;
}

.toolbar-stats__nav {
  display: none; /* ocultas por default; el media query las muestra */
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--kln-text-muted);
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}
.toolbar-stats__nav:hover {
  background: var(--kln-border-dim);
  color: var(--kln-text);
}

/* Viewport angosto: limitar el ancho del scroll a 1 chip y mostrar las
   flechas. Antes era 150 px (~2 chips) pero el operador prefiere
   sacrificar visibilidad de chips a cambio de que los botones del
   grupo central — entidades, filtros, modos — se vean completos sin
   scroll horizontal. Las flechas siguen navegando los 5 chips. */
@media (max-width: 1500px) {
  .toolbar-stats__scroll {
    max-width: 80px;
  }
  .toolbar-stats__nav {
    display: inline-flex;
  }
  /* Centro: cambiar `flex-wrap: wrap` por `nowrap` + scroll horizontal.
     Antes el wrap hacía que el grupo central creciera verticalmente y la
     toolbar tapaba la parte alta del sidebar/drawer. Ahora se queda en
     una sola línea; si no cabe todo, el operador hace scroll con trackpad
     (los toggles principales — entidades, filtros — siempre quedan al
     inicio, los menos usados al final). */
  .toolbar-center-group {
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    justify-content: flex-start;
    min-width: 0;
  }
  .toolbar-center-group::-webkit-scrollbar { display: none; }
}
</style>

<!-- Global (sin scoped): el buscador se inyecta en el header del Dialog,
     fuera del subtree de este componente, así que el scope CSS no le
     aplicaría. backoffice-v2.css estila TODO .v-text-field dentro de
     .v-dialog (padding-top 28px + slot 42px, pensado para forms CRUD) con
     !important y especificidad (0,4,0); estos selectores la superan para
     que el fondo del campo cubra el control completo en el header. -->
<style>
.v-dialog .v-text-field.muni-header-search {
  padding-top: 0 !important;
  height: auto;
}

.v-dialog .v-text-field.muni-header-search > .v-input__control {
  min-height: 34px !important;
}

.v-dialog .v-text-field.muni-header-search > .v-input__control > .v-input__slot {
  height: 34px !important;
  min-height: 34px !important;
  padding: 0 12px !important;
  margin-bottom: 0 !important;
}
</style>
